import { useEffect, useRef, useState } from "react";
import { getPage } from "@/api/pages";
import { getSessionToken } from "@/api/auth";

// Sites round 2 (D17) — a live miniature of the actual page instead of a generic icon.
// Uploaded pages: their own HTML drawn small (scripts off, nothing clickable, loads only when the
// card scrolls into view). Drag-and-drop pages keep a centered icon.
const cache = new Map<string, string>();
const W = 1280; // the width the page is drawn at before shrinking

export const PageThumb = ({ pageId, isUploaded, fallback, width = 120, height = 80 }: { pageId?: string; isUploaded: boolean; fallback?: string; width?: number; height?: number }) => {
  const box = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string | null>(pageId ? cache.get(pageId) ?? null : null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    if (!isUploaded || !pageId || html !== null || !box.current) return;
    const io = new IntersectionObserver(async ([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const r = await getPage(pageId, { token: getSessionToken() });
      const h = r?.data?.htmlContent || "";
      cache.set(pageId, h);
      setHtml(h);
    });
    io.observe(box.current);
    return () => io.disconnect();
  }, [pageId, isUploaded, html]);

  return (
    <div
      ref={box}
      className="relative flex-none overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/[0.06] transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-md group-hover:ring-black/[0.12] dark:ring-white/10"
      style={{ width, height }}
    >
      {isUploaded && !drawn && <div className="absolute inset-0 animate-pulse bg-gray-100 dark:bg-white/5" />}
      {isUploaded && html ? (
        <iframe
          onLoad={() => setDrawn(true)}
          title="Page thumbnail"
          sandbox=""
          srcDoc={html}
          tabIndex={-1}
          aria-hidden
          style={{ width: W, height: (W * height) / width, border: 0, transform: `scale(${width / W})`, transformOrigin: "0 0", pointerEvents: "none", position: "absolute", top: 0, left: 0, opacity: drawn ? 1 : 0, transition: "opacity .3s ease" }}
        />
      ) : isUploaded ? null : (
        <div className="flex h-full w-full items-center justify-center bg-gray-50 dark:bg-white/5">
          {fallback ? <img src={fallback} alt="" className="h-7 w-7 object-contain opacity-80" /> : null}
        </div>
      )}
    </div>
  );
};
